import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import toast from "react-hot-toast";
import { useNotifications } from "../context/NotificationContext.js";
import { LoadingState } from "./motion.js";

const typeMeta = {
  milk_week: { label: "Leche", path: "/providers", tone: "text-emerald-700 bg-emerald-50" },
  milk_day: { label: "Leche", path: "/providers", tone: "text-emerald-700 bg-emerald-50" },
  cheese_week: { label: "Quesos", path: "/cheeses", tone: "text-amber-800 bg-amber-50" },
  cheese_day: { label: "Quesos", path: "/cheeses", tone: "text-amber-800 bg-amber-50" },
};

const timeAgo = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Hace ${days} d`;
  return date.toLocaleString("es-VE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function NotificationBell({ compact = false, align = "right" }) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const { items, unread, loading, permission, fetchInbox, enableBrowser, markRead, markAllRead } =
    useNotifications();
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);

  const placePanel = useCallback(() => {
    const node = buttonRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = Math.min(352, window.innerWidth - 24);
    let left = align === "left" ? rect.left : rect.right - width;
    left = Math.max(12, Math.min(left, window.innerWidth - width - 12));
    const spaceBelow = window.innerHeight - rect.bottom;
    const openDown = compact || spaceBelow > 280;
    setPanelPos({
      width,
      left,
      top: openDown ? rect.bottom + 8 : undefined,
      bottom: openDown ? undefined : window.innerHeight - rect.top + 8,
    });
  }, [align, compact]);

  useEffect(() => {
    if (!open) return undefined;
    placePanel();
    fetchInbox();
    const onClick = (event) => {
      const target = event.target;
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onResize = () => placePanel();
    window.addEventListener("mousedown", onClick);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, fetchInbox, placePanel]);

  const handleEnableBrowser = async (event) => {
    event.stopPropagation();
    await enableBrowser();
  };

  const handleOpenItem = async (item) => {
    const meta = typeMeta[item.type] || { path: "/" };
    if (!item.read) {
      try {
        await markRead(item._id);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    navigate(meta.path);
  };

  const handleMarkAll = async () => {
    try {
      await markAllRead();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudieron marcar como leídas", {
        position: "top-center",
      });
    }
  };

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: panelPos.top,
              bottom: panelPos.bottom,
              left: panelPos.left,
              width: panelPos.width,
            }}
            className="bg-white rounded-xl border border-gray-200 shadow-xl z-[80] overflow-hidden"
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Notificaciones</p>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAll}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                >
                  Marcar leídas
                </button>
              )}
            </div>
            {permission === "default" && (
              <button
                type="button"
                onClick={handleEnableBrowser}
                className="w-full text-left px-3 py-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-b border-indigo-100"
              >
                Activar avisos del navegador
              </button>
            )}
            {permission === "denied" && (
              <p className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                Los avisos del navegador están bloqueados para este sitio.
              </p>
            )}
            <div className="reveal-host max-h-[min(24rem,60vh)] overflow-y-auto">
              {loading && items.length === 0 ? (
                <LoadingState variant="compact" count={4} label="Cargando notificaciones" />
              ) : items.length === 0 ? (
                <p className="px-3 py-8 text-sm text-center text-gray-400">
                  No hay notificaciones todavía.
                </p>
              ) : (
                items.map((item) => {
                  const meta = typeMeta[item.type] || {
                    label: "Aviso",
                    tone: "text-gray-700 bg-gray-100",
                  };
                  return (
                    <button
                      key={item._id}
                      type="button"
                      onClick={() => handleOpenItem(item)}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${
                        item.read ? "bg-white" : "bg-indigo-50/60"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-[11px] text-gray-400 ml-auto">
                          {timeAgo(item.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 leading-snug">{item.title}</p>
                      <p className="text-xs text-gray-500 mt-1 whitespace-pre-line line-clamp-3">
                        {item.body}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={async () => {
          if (!open && permission === "default") {
            await enableBrowser();
          }
          setOpen((prev) => !prev);
        }}
        className={
          compact
            ? "relative p-2 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-gray-100"
            : "relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
        }
        title="Notificaciones"
        aria-label="Notificaciones"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-4 text-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {panel}
    </div>
  );
}
